<template>
  <div :class="[bem.b()]" :style="{ gap: gap + 'px' }">
    <div
      v-for="(color, index) in colors || []"
      :key="index"
      :class="[
        bem.e('color-item'),
        readonly ? bem.is('readonly') : '',
        bem.is(size),
        shape === 'round' ? bem.is('round') : bem.is('square'),
        isActive(color) ? bem.is('item-active') : '',
        isDisabled(color) ? bem.is('disabled') : '',
      ]"
      :style="{ backgroundColor: color }"
      @click="handleColorSelect(color)"
    >
      <div
        :class="['inner-content']"
        :style="{ borderColor: isActive(color) ? '#fff' : 'transparent' }"
      >
        <div class="inner" :style="{ backgroundColor: 'transparent' }"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { createNamespace } from '@ll-plus/utils'
import { computed, watch } from 'vue'
import {
  colorPresetPickerEmits,
  colorPresetPickerProps,
} from './color-preset-picker'

defineOptions({ name: 'LlColorPresetPicker' })

const props = defineProps(colorPresetPickerProps)
const emits = defineEmits(colorPresetPickerEmits)

const bem = createNamespace('color-preset-picker')

// 激活状态
const isActive = computed(() => {
  return (color: string) => {
    if (props.multiple) {
      return Array.isArray(props.value) && props.value.includes(color)
    }
    return props.value === color
  }
})

// 是否禁用
const isDisabled = computed(() => {
  return (color: string) => {
    if (!props.multiple || !Array.isArray(props.value)) return false
    if (
      !props.value?.includes(color) &&
      props.maxCount > 0 &&
      props.value.length >= props.maxCount
    ) {
      return true
    }
    return false
  }
})

// 处理颜色选择
const handleColorSelect = (color: string) => {
  if (props.readonly) return
  // 多选
  if (props.multiple && Array.isArray(props.value)) {
    const currentValues = [...props.value]
    const colorIndex = currentValues.indexOf(color)

    if (colorIndex > -1) {
      // 如果颜色已选中，则移除
      currentValues.splice(colorIndex, 1)
    } else {
      // 超过选中上限则不执行
      if (props.maxCount > 0 && props.value.length >= props.maxCount) return

      // 如果颜色未选中，则添加
      currentValues.push(color)
    }
    emits('update:value', currentValues)
    emits('change', currentValues)
  }
  // 单选
  else {
    emits('update:value', color === props.value ? undefined : color)
    emits('change', color === props.value ? undefined : color)
  }
}

watch(
  () => props.multiple,
  (multiple) => {
    let nextValue: string | string[] | undefined

    // 单选 → 多选
    if (multiple) {
      if (Array.isArray(props.value)) return
      nextValue = props.value == null ? [] : [props.value as string]
    }

    // 多选 → 单选
    else {
      if (!Array.isArray(props.value)) return
      nextValue = props.value.length > 0 ? props.value[0] : undefined
    }

    emits('update:value', nextValue)
  },
  { flush: 'post' },
)
watch(
  () => props.value,
  (val) => {
    let nextValue: string | string[] | undefined
    // 多选
    if (props.multiple) {
      if (Array.isArray(val)) return
      nextValue = val == null ? [] : [val as string]
    }
    // 单选
    else {
      if (Array.isArray(val)) {
        nextValue = val[0] ?? undefined
      } else if (val == null) {
        nextValue = undefined
      } else if (typeof val !== 'string') {
        nextValue = String(val)
      } else {
        return
      }
    }

    if (props.multiple) {
      if (
        Array.isArray(val) &&
        JSON.stringify(val) === JSON.stringify(nextValue)
      )
        return
    } else {
      if (val === nextValue) return
    }

    emits('update:value', nextValue)
  },
  { immediate: true, flush: 'post' },
)
</script>
